import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { Icon, TableHeaderCell, TapTarget } from "../../ui";
import type { SortDirection, SortKey } from "../types";

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  direction: SortDirection;
  align?: "left" | "right";
  className?: string;
  onToggle: (key: SortKey) => void;
}

/**
 * Table-header cell with a sort toggle. The visible glyph reflects the
 * current state (asc/desc/none) and the `aria-sort` attribute mirrors
 * it for assistive tech. Clicking cycles asc → desc → asc; clicking a
 * different column resets to asc on the new key (caller's `onToggle`
 * decides).
 */
export function SortableHeader({
  label,
  columnKey,
  activeKey,
  direction,
  align = "left",
  className,
  onToggle,
}: SortableHeaderProps) {
  const active = activeKey === columnKey;
  const iconName = active
    ? direction === "asc"
      ? "chevron-up"
      : "chevron-down"
    : "chevrons-up-down";
  const ariaSort = active ? (direction === "asc" ? "ascending" : "descending") : "none";
  const title = active
    ? direction === "asc"
      ? t.settlement.expenses.table.sortAsc(label)
      : t.settlement.expenses.table.sortDesc(label)
    : t.settlement.expenses.table.sortNone(label);
  return (
    <TableHeaderCell align={align} aria-sort={ariaSort} className={className}>
      <TapTarget
        onClick={() => onToggle(columnKey)}
        title={title}
        aria-label={title}
        className={cn(
          "inline-flex items-center gap-1 font-medium text-ink-500 uppercase tracking-wide transition-colors hover:text-ink-900",
          align === "right" && "flex-row-reverse",
        )}
      >
        <span>{label}</span>
        <Icon name={iconName} size="xs" className={active ? "text-ink-900" : "text-ink-400"} />
      </TapTarget>
    </TableHeaderCell>
  );
}
